/**
 * `GamesPageTemplate` / `GameDetailPageTemplate` 이 그리는 sticky 띠의 **단일 출처**.
 *
 * ## 왜 모았나 (2026-09-08)
 *
 * 「띠 바닥 y」를 재는 코드가 **세 곳**으로 늘어나던 참이었다 — 모바일 검색줄
 * (`useStickyBandBottom`) · 도킹 임계값(`usePcSearchDock`) · 확정 후 스크롤
 * (`scrollToResults`). 세 번째를 쓰면서 `getBoundingClientRect()` 로 **다른 공식**을
 * 만들었는데, 같은 값을 두 공식으로 재면 한쪽만 고쳐도 조용히 갈라진다.
 *
 * ## 공식은 **「붙었을 때의 바닥」**이다 — 지금 붙었는지는 묻지 않는다
 *
 * ```
 *   bottom = 인라인 top + offsetHeight
 * ```
 *
 * 띠 자신이 `sticky` 이고 그 `top` 은 스크롤에 따라 움직인다(`GamesPageTemplate`:
 * `stickyTop = hidden ? 0 : headerHeight`). 그래서 상수가 될 수 없다. 390폭 실측
 * (2026-09-08):
 *
 * ```
 *              인라인 top   높이    바닥     화면에서 가리는 것
 *   도킹 전     175         48      223      헤더(175) + 탭 줄(48)
 *   도킹 후       0        120      120      탭 줄(48) + 검색·필터 줄(72) — 헤더는 뒤로 접힌다
 *   1440         0         49        49
 * ```
 *
 * ⚠️ **`getBoundingClientRect().bottom` 을 쓰면 안 된다.** 아직 안 붙었을 때 그 값은
 * 「지금 문서 어디에 있나」라서 화면 가림과 무관하다 — 그걸 「가림 0」으로 읽었다가
 * 결과 바를 헤더 뒤(화면 top 0)로 보냈다(실측: 결과바 top 0 · 띠 bottom 295).
 *
 * ## `data-testid` 를 프로덕션 선택자로 쓴다
 *
 * ⚠️ 이름이 테스트용으로 읽히지만 **프로덕션 계약**이다. 지우면 `querySelector` 가 null 을
 * 돌려주고 소비처가 조용히 0 을 쓴다 — 화면은 렌더되고 tsc·eslint 도 통과한다.
 * `__tests__/components/sticky-band-selector-contract.test.ts` 가 정의(템플릿)와
 * 소비(이 모듈 단일화) 양쪽을 잠근다.
 */
export const STICKY_BAND_SELECTOR = "games-sticky-tabbar";

/** 띠 요소. 없으면 null (테스트 · 이 띠가 없는 템플릿). */
export function stickyBandEl(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    `[data-testid="${STICKY_BAND_SELECTOR}"]`,
  );
}

/**
 * **붙었을 때의 바닥** — 인라인 `top` + 높이. 요소가 없으면 0.
 *
 * ★띠 전용이 아니다. 이 페이지의 sticky 는 **쌓인다**:
 *
 * ```
 *   띠(games-sticky-tabbar)        top = 헤더 높이(스크롤에 따라 0)
 *   검색·필터 줄(모바일)            top = 띠의 stuckBottom
 *   결과 툴바(개수·정렬·보기)       top = 검색줄의 stuckBottom   ← #5561
 * ```
 *
 * 세 층 다 **인라인 `top` + `offsetHeight`** 라는 같은 공식이라 하나로 둔다.
 */
export function stuckBottom(el: HTMLElement | null) {
  if (!el) return 0;
  const top = Number.parseFloat(el.style.top || "0") || 0;
  return top + el.offsetHeight;
}

/** 띠가 **붙었을 때** 화면 위를 가리는 높이. 띠가 없으면 0. */
export function stickyBandBottom(el: HTMLElement | null = stickyBandEl()) {
  return stuckBottom(el);
}

/**
 * 모바일 검색·필터 줄의 선택자 (#5561).
 *
 * ⚠️ 띠와 같은 이유로 **프로덕션 계약**이다 — 지우면 결과 툴바가 조용히 0 을 써서
 * 검색줄 **뒤에 겹친다**(화면은 렌더되고 tsc·eslint 도 통과). 계약 가드가 잠근다.
 */
export const MOBILE_FILTER_ROW_SELECTOR = "games-mobile-filter-row";

/**
 * 모바일 검색·필터 줄 — **지금 화면을 덮고 있을 때만** 돌려준다.
 *
 * ★술어가 둘이다. 한쪽만 보면 두 탭 중 하나가 틀린다 (#5499 · 2026-09-08):
 *
 * ```
 *                       데스크탑에서                   판별
 *   VR   `laptop:hidden`   display:none → offsetParent null   ← 보임 여부
 *   PC   `laptop:static`   **보이지만** 고정이 아니다          ← position 여부
 * ```
 *
 * 종전엔 `offsetParent` 만 봤다. VR 만 있을 때는 맞았지만 PC 가 같은 표식을 쓰면서
 * **데스크탑에서 이 줄이 보이는데 고정은 아닌** 조합이 생겼다 — 그때 이 함수가 요소를
 * 돌려주면 가림 높이가 과대계산된다.
 */
export function mobileFilterRowEl(): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(
    `[data-testid="${MOBILE_FILTER_ROW_SELECTOR}"]`,
  );
  if (!el || el.offsetParent === null) return null; // 숨겨져 있으면 층이 없다
  return getComputedStyle(el).position === "sticky" ? el : null;
}
